import { useRef } from 'react'
import type { ReactNode } from 'react'
import styled from 'styled-components'
import type { Profile } from 'models/ota/ProfileModel'
import { theme } from 'styles/theme'
import { AnchoredOverlay } from '../../AnchoredOverlay/AnchoredOverlay'
import { Badge, BadgeColor } from '../../Badge/Badge'
import { GhostButton } from '../../Button/Button'
import { SvgIcons } from '../../Icon/Icon'
import { RoundRadioButton } from '../../RoundRadioButton/RoundRadioButton'

const Card = styled.label`
    display: flex;
    align-items: center;
    gap: 0.75em;
    min-block-size: 3em;
    padding: 0 0.75em;
    border: 1px solid ${theme.colors.MediumGrey};
    border-radius: ${theme.radii.medium};
    background-color: ${theme.colors.White};
`

const ProfileTitle = styled.span`
    font-size: ${theme.fontSizes.small};
`

const ProfileBadge = styled(Badge)`
    padding: 0.15em 0.6em;
    font-size: 0.75em;
    font-weight: ${theme.fontWeights.bold};
`

export const InstalledBadge = () => <ProfileBadge {...{ text: 'Installed', badgeColor: BadgeColor.Green }} />
export const LatestBadge = () => <ProfileBadge {...{ text: 'Latest', badgeColor: BadgeColor.Orange }} />

// The current profile card, when the installed profile is not known
export const UnknownProfileCard = () =>
    <Card {...{ as: 'div' }}>
        <ProfileBadge {...{ text: 'Unknown', badgeColor: BadgeColor.Red }} />
    </Card>

// The current profile card, when the vehicles have different installed profiles
export const MixedProfileCard = () =>
    <Card {...{ as: 'div' }}>
        <ProfileBadge {...{ text: 'Mixed', badgeColor: BadgeColor.Normal }} />
    </Card>

const ChevronButton = styled(GhostButton)`
    --size: 1em;
    margin-inline-start: auto;
`

const Chevron = styled(SvgIcons.ChevronSvg)<{ $isOpen: boolean }>`
    transform: rotate(${({ $isOpen }) => $isOpen ? '180deg' : '0'});
`

interface ProfileCardProps {
    profile: Pick<Profile, 'id' | 'title'>
    badge?: ReactNode
    // The available versions are radio buttons. The installed profile card has no radio button.
    radio?: { name: string, isChecked: boolean, onChecked: VoidFunction }
    // Shown in the overlay below the card while it is open
    moduleVersions: ReactNode
    isOpen: boolean
    onToggle: VoidFunction
}

// A profile card, that opens the module versions of the profile in an overlay below it
export const ProfileCard = ({ profile, badge, radio, moduleVersions, isOpen, onToggle }: ProfileCardProps) => {
    const cardRef = useRef<HTMLLabelElement>(null)
    return (
        <>
            <Card {...{ ref: cardRef, as: radio ? 'label' : 'div' }}>
                { radio &&
                    <RoundRadioButton {...{ name: radio.name, value: profile.id, checked: radio.isChecked, onChange: radio.onChecked }} />
                }
                <ProfileTitle>{ profile.title }</ProfileTitle>
                { badge }
                <ChevronButton {...{
                    text: isOpen ? 'Hide module versions' : 'Show module versions',
                    'aria-expanded': isOpen,
                    onClick: (e) => {
                        // The button is inside the card's label, so don't let the click also select the radio button
                        e.preventDefault()
                        onToggle()
                    },
                }}>
                    <Chevron {...{ $isOpen: isOpen }} />
                </ChevronButton>
            </Card>
            { isOpen &&
                <AnchoredOverlay {...{ anchorRef: cardRef, onClose: onToggle }}>
                    { moduleVersions }
                </AnchoredOverlay>
            }
        </>
    )
}
